import * as React from 'react';
import { LogOut, PanelLeft, type LucideIcon } from 'lucide-react';

import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

export interface AppShellProps {
  appName: string;
  appSubtitle?: string;
  appNameAccent?: string;
  navSections: NavSection[];
  currentPath: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onNavigate: (to: string) => void;
  userEmail?: string;
  onLogout: () => void;
  pageTitle?: string;
  children: React.ReactNode;
}

const SIDEBAR_WIDTH = 'w-64';
const SIDEBAR_WIDTH_COLLAPSED = 'w-16';

function isActive(itemPath: string, currentPath: string) {
  if (itemPath === '/') return currentPath === '/';
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
}

export function AppShell({
  appName,
  appSubtitle,
  appNameAccent,
  navSections,
  currentPath,
  collapsed,
  onToggleCollapsed,
  onNavigate,
  userEmail,
  onLogout,
  pageTitle,
  children,
}: AppShellProps) {
  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside
        className={cn(
          'border-r border-sidebar-border bg-sidebar text-sidebar-foreground flex flex-col transition-[width] duration-200 ease-in-out',
          collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
        )}
        aria-label="Navegación principal"
      >
        <div
          className={cn(
            'h-16 flex items-center border-b border-sidebar-border px-3 gap-2',
            collapsed && 'justify-center px-0',
          )}
        >
          {collapsed ? (
            <div className="flex size-9 items-center justify-center bg-white/5 text-sidebar-primary font-[family-name:var(--font-wordmark)] font-bold text-base lowercase">
              f
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="flex flex-col leading-tight overflow-hidden min-w-0">
                <span className="font-[family-name:var(--font-wordmark)] font-bold text-[20px] leading-none tracking-[-0.01em] lowercase truncate">
                  {appName}
                  {appNameAccent && (
                    <>
                      {' '}
                      <span className="text-sidebar-primary">{appNameAccent}</span>
                    </>
                  )}
                </span>
                {appSubtitle && (
                  <span className="mt-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-sidebar-foreground/60 truncate">
                    {appSubtitle}
                  </span>
                )}
              </div>
            </div>
          )}
          {!collapsed && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onToggleCollapsed}
              aria-label="Colapsar menú"
              title="Colapsar menú"
              className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/5"
            >
              <PanelLeft />
            </Button>
          )}
        </div>
        {collapsed && (
          <div className="flex justify-center pt-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onToggleCollapsed}
              aria-label="Expandir menú"
              title="Expandir menú"
              className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/5"
            >
              <PanelLeft />
            </Button>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto py-3">
          {navSections.map((section, idx) => (
            <div key={idx} className={cn('px-2', idx > 0 && 'mt-5')}>
              {section.title && !collapsed && (
                <div className="px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-sidebar-foreground/50">
                  {section.title}
                </div>
              )}
              <ul className="flex flex-col gap-0.5">
                {section.items.map((item) => {
                  const active = isActive(item.to, currentPath);
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <button
                        type="button"
                        onClick={() => onNavigate(item.to)}
                        title={collapsed ? item.label : undefined}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'group relative w-full flex items-center gap-3 px-3 py-2 text-[13px] font-semibold outline-none transition-colors',
                          'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/5',
                          'focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                          active &&
                            'bg-sidebar-accent text-sidebar-accent-foreground',
                          collapsed && 'justify-center px-0',
                        )}
                      >
                        {active && !collapsed && (
                          <span
                            aria-hidden
                            className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 bg-destructive"
                          />
                        )}
                        <Icon
                          className={cn(
                            'size-4 shrink-0',
                            active && 'text-sidebar-primary',
                          )}
                        />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div
          className={cn(
            'border-t border-sidebar-border p-3',
            collapsed && 'flex justify-center',
          )}
        >
          {collapsed ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onLogout}
              aria-label="Cerrar sesión"
              title={userEmail ? `${userEmail} — Cerrar sesión` : 'Cerrar sesión'}
              className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/5"
            >
              <LogOut />
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex size-8 shrink-0 items-center justify-center bg-sidebar-accent text-sidebar-accent-foreground text-xs font-bold">
                {(userEmail ?? '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex flex-col leading-tight overflow-hidden flex-1 min-w-0">
                <span className="text-[12px] font-semibold truncate">
                  {userEmail ?? 'Sin sesión'}
                </span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-sidebar-foreground/50">
                  Cliente
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onLogout}
                aria-label="Cerrar sesión"
                title="Cerrar sesión"
                className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-white/5"
              >
                <LogOut />
              </Button>
            </div>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="relative h-14 border-b border-border flex items-center px-8 bg-background">
          <h1 className="text-[14px] font-bold tracking-tight">
            {pageTitle ?? appName}
          </h1>
          <span
            aria-hidden
            className="pointer-events-none absolute left-8 bottom-0 h-[1.5px] w-8 bg-destructive"
          />
        </header>
        <main className="flex-1 overflow-y-auto p-8 bg-background">{children}</main>
      </div>
    </div>
  );
}
